import React, { useState } from 'react';

import CardDialog from '~/components/CardDialog';
import MenuItem from '~/components/core/Atomic/MenuItem';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import Text from '~/components/core/TextComponents/Text';
import Switch from '~/components/ui/atoms/Switch';
import type {
  AvailableRecoveryProviderInfoList,
  AvailableRecoveryProviderInfoListProviderConfigFieldInfo,
  RecoveryRequestsConfigInfo,
  UpdateRecoveryRequestsConfigParams,
} from '~/server_shared/openapi/data-contracts';

export interface RecoveryRequestsConfigurationDialogProps {
  /** Display name of what is being configured: the organization, or the sub-organization. */
  configuredForName: string;
  /** What the PUT writes: null for the organization-level row, the sub-org's id otherwise. */
  subOrganizationId: number | null;
  /** The row this scope has of its own, or undefined when it has none. */
  config?: RecoveryRequestsConfigInfo;
  /**
   * What this scope follows today while it has no row of its own — the organization-level row.
   * Undefined at organization scope, and for a sub-org of an organization that has no row either.
   */
  inheritedConfig?: RecoveryRequestsConfigInfo;
  availableProviders: AvailableRecoveryProviderInfoList;
  onSubmit: (params: UpdateRecoveryRequestsConfigParams) => Promise<void>;
  onCancel: () => void;
}

const RecoveryRequestsConfigurationDialog: React.FC<RecoveryRequestsConfigurationDialogProps> = ({
  configuredForName,
  subOrganizationId,
  config,
  inheritedConfig,
  availableProviders,
  onSubmit,
  onCancel,
}) => {
  // Every field is derived from the props with a local override rather than seeded into state on
  // mount: the dialog opens from the table, which renders before either GET has landed, and a value
  // captured by useState then would never pick the response up. The override takes over the moment
  // the operator touches the field.
  const effectiveConfig = config ?? inheritedConfig;
  const isInherited = !config && Boolean(inheritedConfig);

  const [enabledOverride, setEnabledOverride] = useState<boolean | undefined>(undefined);
  const isEnabled = enabledOverride ?? Boolean(effectiveConfig?.is_enabled);

  const storedProviderKey = effectiveConfig?.provider_key;
  // A stored key whose provider module is no longer registered on this deployment has no MenuItem
  // to select, and update_config runs assert_registered on every write — so offering it back would
  // leave the picker blank and 400 the save, even one that only turns the row off.
  const isStoredProviderRegistered =
    storedProviderKey !== undefined &&
    availableProviders.some((provider) => provider.provider_key === storedProviderKey);
  const [providerKeyOverride, setProviderKeyOverride] = useState<string | undefined>(undefined);
  // Seeded from the first offered provider rather than a literal 'manual': the option set comes
  // from the backend registry, so no provider key is spelled out on this side of the wire.
  const providerKey =
    providerKeyOverride ?? (isStoredProviderRegistered ? storedProviderKey : availableProviders[0]?.provider_key) ?? '';

  // Which per-scope values the selected handler needs, and what they are called, both come from the
  // registry listing the picker is already built from. Nothing here knows what any of them mean, so
  // a handler landing with a different set of fields needs no change on this side.
  const configurationFields =
    availableProviders.find((provider) => provider.provider_key === providerKey)?.configuration_fields ?? [];

  // The row carries the values of the handler it names, so a picker set to a different one must not
  // pre-fill from them: two handlers may declare the same key and mean unrelated things by it.
  const resolvedProviderConfig = storedProviderKey === providerKey ? effectiveConfig?.provider_config : undefined;
  // ...and neither must values this scope does not own. The backend resolves provider_config the way
  // a send does - the scope's own values, else the organization's - so a sub-org that stores nothing
  // of its own reads back the organization's. Pre-filling those would make the first edit of any
  // *other* field submit them as this scope's own, pinning it to whatever the organization held that
  // day: the write replaces the whole declared set, so every field travels with the one that changed.
  // The values are shown as help text below instead, where they inform without being submitted.
  const isProviderConfigInherited = isInherited || Boolean(effectiveConfig?.provider_config_is_inherited);
  const ownProviderConfig = isProviderConfigInherited ? undefined : resolvedProviderConfig;
  const inheritedProviderConfig = isProviderConfigInherited ? resolvedProviderConfig : undefined;
  const [providerConfigOverride, setProviderConfigOverride] = useState<Record<string, string>>({});
  const displayedValue = (key: string) => providerConfigOverride[key] ?? ownProviderConfig?.[key] ?? '';

  const isBlankRequiredField = (field: AvailableRecoveryProviderInfoListProviderConfigFieldInfo) =>
    Boolean(field.is_required) && !displayedValue(field.key).trim();

  // What this scope runs under today without owning it. Named on the field rather than pre-filled
  // into it, so nothing the operator did not type is submitted - and a blank required input is
  // explained rather than merely disabling Save.
  const helperTextFor = (field: AvailableRecoveryProviderInfoListProviderConfigFieldInfo) => {
    const inheritedValue = inheritedProviderConfig?.[field.key];
    const inheritedNote = inheritedValue ? `Currently inherited from the organization: ${inheritedValue}` : undefined;
    const requiredNote = isEnabled && isBlankRequiredField(field) ? `${field.label} is required.` : undefined;
    // The message displaces the handler's own help text while the field is invalid, which is what
    // TextFieldFormik does with a validation error.
    return [requiredNote ?? field.help_text ?? undefined, inheritedNote].filter(Boolean).join(' ') || undefined;
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit({
        is_enabled: isEnabled,
        provider_key: providerKey,
        sub_organization_id: subOrganizationId,
        // Sent only once the operator has edited one of the fields, and then as the *whole* declared
        // set rather than the edits alone: the write replaces the handler's stored values for this
        // scope, while omitting the key entirely leaves them alone. What travels is this scope's own
        // values plus the edits - never the organization's, which displayedValue withholds - so a
        // save states exactly the configuration the operator chose for the scope in front of them.
        ...(Object.keys(providerConfigOverride).length > 0
          ? {
              provider_config: Object.fromEntries(
                configurationFields.map((field) => [field.key, displayedValue(field.key)])
              ),
            }
          : {}),
      });
    } catch {
      // The caller has already reported the failure; the dialog stays open so the operator can
      // retry. Nothing clears the flag on the success path — onSubmit closes the dialog first.
      setIsSubmitting(false);
    }
  };

  return (
    <CardDialog
      isDialog
      onClose={onCancel}
      title={`Recovery Requests - ${configuredForName}`}
      preventClose={isSubmitting}
      footerActions={
        <DialogFooterActions
          disabled={isSubmitting}
          // Save only: an empty key would reach the PUT, and assert_registered refuses it. A blank
          // required value is refused the same way, but only for a scope being turned on — the
          // backend does not ask for one to turn a scope off. Cancel has to stay reachable
          // regardless.
          primaryDisabled={!providerKey || (isEnabled && configurationFields.some(isBlankRequiredField))}
          onClickPrimary={handleSave}
          onClickSecondary={onCancel}
          primaryLabel="Save"
          secondaryLabel="Cancel"
        />
      }
    >
      <div className="flex flex-col gap-16 py-8">
        {isInherited ? (
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            {`${configuredForName} has no configuration of its own and currently follows the organization-level one shown below. Saving creates a configuration for ${configuredForName} that stops following it.`}
          </Text>
        ) : null}

        {storedProviderKey !== undefined && !isStoredProviderRegistered ? (
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            {`The configured handler "${storedProviderKey}" is not registered on this environment. Saving replaces it with the handler selected below.`}
          </Text>
        ) : null}

        <div className="flex items-center gap-8">
          <Switch
            checked={isEnabled}
            onChange={(_event, checked) => setEnabledOverride(checked)}
            disabled={isSubmitting}
            aria-label="Enable recovery requests"
          />
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            Enable recovery requests
          </Text>
        </div>

        <SingleSelectField<string>
          label="Handler"
          value={providerKey}
          fullWidthField
          disabled={isSubmitting || !isEnabled}
          onChange={(value) => {
            setProviderKeyOverride(value ?? '');
            // Edits belong to the handler they were typed for: carrying them across would submit
            // one handler's value under another's identically-named key.
            setProviderConfigOverride({});
          }}
        >
          {availableProviders.map((provider) => (
            <MenuItem key={provider.provider_key} value={provider.provider_key}>
              {provider.display_name}
            </MenuItem>
          ))}
        </SingleSelectField>

        {configurationFields.map((field) => (
          <TextField<string>
            key={field.key}
            // Wires the floating label to the input: TextField hands its own `htmlFor` through as
            // whatever `id` it was given, so without one the label labels nothing. Namespaced
            // because the key is the provider's to choose and this page renders other cards: a bare
            // `template_code` would collide with any element already holding that id.
            id={`recovery-request-provider-config-${field.key}`}
            label={field.label}
            value={displayedValue(field.key)}
            fullWidthField
            disabled={isSubmitting || !isEnabled}
            error={isEnabled && isBlankRequiredField(field)}
            helperText={helperTextFor(field)}
            onChange={(newValue) => setProviderConfigOverride((current) => ({ ...current, [field.key]: newValue }))}
          />
        ))}
      </div>
    </CardDialog>
  );
};

export default RecoveryRequestsConfigurationDialog;
